import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import "./UpdateBlockPopup.css";
import CancelReasonPopup from "./CancelReasonPopup";

const STATUS_OPTIONS = [
  { code: "DEF", label: "Definite" },
  { code: "ACT", label: "Actual" },
  { code: "CXL", label: "Cancel" },
  { code: "LOS", label: "Lost" },
];

const STATUS_LABELS = {
  TEN: "Tentative",
  DEF: "Definite",
  ACT: "Actual",
  CXL: "Cancel",
  LOS: "Lost",
};

const UpdateBlockPopup = ({ show, block, onClose, onUpdate, updating = false }) => {
  const [status, setStatus] = useState("");
  const [activeReasonLookup, setActiveReasonLookup] = useState(null);
  const [form, setForm] = useState({
    blockCancelReason: "",
    description: "",
    cancelPmReservations: true,
    pmCancelReason: "",
    pmDescription: "",
  });

  useEffect(() => {
    if (!show || !block) return;
    setStatus("CXL");
    setActiveReasonLookup(null);
    setForm({
      blockCancelReason: "",
      description: "",
      cancelPmReservations: true,
      pmCancelReason: "",
      pmDescription: "",
    });
  }, [show, block]);

  useEffect(() => {
    if (!show) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape" && !activeReasonLookup) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.classList.remove("modal-open");
    };
  }, [show, onClose, activeReasonLookup]);

  if (!show || !block) return null;

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onUpdate?.({
      OPID: block.OPID,
      BlockStatus: STATUS_LABELS[status] || status,
      BlockCancelCode: form.blockCancelReason,
      Description: form.description,
      CancelPMReservations: form.cancelPmReservations,
      PMCancelCode: form.pmCancelReason,
      PMDescription: form.pmDescription,
    });
  };

  const selectCancelReason = (reason) => {
    if (activeReasonLookup === "block") {
      updateField("blockCancelReason", reason.Code);
      updateField("description", reason.Description);
    } else if (activeReasonLookup === "pm") {
      updateField("pmCancelReason", reason.Code);
      updateField("pmDescription", reason.Description);
    }
    setActiveReasonLookup(null);
  };

  const isCancellation = status === "CXL" || status === "LOS";

  return (
    <div
      className="modal fade show d-block update-block-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="updateBlockModalTitle"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header update-block-header">
              <h5 id="updateBlockModalTitle" className="modal-title text-white">Change Block Status</h5>
              <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={onClose} />
            </div>

            <div className="modal-body update-block-body">
              <div className="row g-0 update-block-summary">
                <div className="col-md-4">
                  <span>Block Name</span>
                  <strong>{block.BlockName || "-"}</strong>
                </div>
                <div className="col-md-3">
                  <span>Block Code</span>
                  <strong>{block.BlockCode || "-"}</strong>
                </div>
                <div className="col-md-5">
                  <span>Current Block Status</span>
                  <strong>{STATUS_LABELS[block.BlockStatus] || block.BlockStatus || "-"}</strong>
                </div>
              </div>

              <div className="row g-4 update-status-section">
                <div className="col-md-6">
                  <h6>Select a Block Status</h6>
                  <div className="update-status-options">
                    {STATUS_OPTIONS.map((option) => (
                      <label key={option.code} className={status === option.code ? "active" : ""}>
                        <input type="radio" name="blockStatus" value={option.code} checked={status === option.code} onChange={(event) => setStatus(event.target.value)} />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="col-md-6 update-room-status">
                  <h6>Room Status Details</h6>
                  <div className="row">
                    <div className="col-6">
                      <span>Status</span>
                      <strong>{STATUS_LABELS[status] || status || "-"}</strong>
                    </div>
                    <div className="col-6">
                      <span>Inventory</span>
                      <strong>{isCancellation ? "Return to Inventory" : "Block Inventory"}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <section className="update-cancel-section">
                <div className="update-section-toggle">
                  <span>Cancellation Details</span>
                </div>

                <div className="row g-4 update-cancel-content">
                      <div className="col-lg-4">
                        <label className="form-label">Block Cancel Reason <span>*</span></label>
                        <div className="input-group input-group-sm update-search-field">
                          <input className="form-control" value={form.blockCancelReason} onChange={(event) => updateField("blockCancelReason", event.target.value)} onFocus={() => setActiveReasonLookup("block")} placeholder="Enter reason code" />
                          <button type="button" className="btn" aria-label="Search block cancel reason" onClick={() => setActiveReasonLookup("block")}><Search aria-hidden="true" /></button>
                        </div>
                       
                      </div>

                      <div className="col-lg-4 update-cancel-column">
                        <label className="form-label">Description <span>*</span></label>
                        <input className="form-control form-control-sm" value={form.description} onChange={(event) => updateField("description", event.target.value)} placeholder="Cancellation description" />
                      </div>

                      <div className="col-lg-4 update-cancel-column">
                        <label className="form-check update-pm-check">
                          <input className="form-check-input" type="checkbox" checked={form.cancelPmReservations} onChange={(event) => updateField("cancelPmReservations", event.target.checked)} />
                          <span className="form-check-label">Cancel PM Reservations</span>
                        </label>
                        <label className="form-label">PM Cancel Reason <span>*</span></label>
                        <div className="input-group input-group-sm update-search-field">
                          <input className="form-control" value={form.pmCancelReason} onChange={(event) => updateField("pmCancelReason", event.target.value)} onFocus={() => setActiveReasonLookup("pm")} placeholder="Enter PM reason code" />
                          <button type="button" className="btn" aria-label="Search PM cancel reason" onClick={() => setActiveReasonLookup("pm")}><Search aria-hidden="true" /></button>
                        </div>
                        <label className="form-label mt-3">Description</label>
                        <input className="form-control form-control-sm" value={form.pmDescription} onChange={(event) => updateField("pmDescription", event.target.value)} placeholder="PM cancellation description" />
                      </div>
                </div>
              </section>
            </div>

            <div className="modal-footer update-block-footer">
              <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={!status || updating}>{updating ? "Updating..." : "Update Block Status"}</button>
            </div>

            <CancelReasonPopup
              show={Boolean(activeReasonLookup)}
              title={activeReasonLookup === "pm" ? "Select PM Cancel Reason" : "Select Block Cancel Reason"}
              onClose={() => setActiveReasonLookup(null)}
              onSelect={selectCancelReason}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateBlockPopup;
